import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MapView from "./components/MapView";
import Controls from "./components/Controls";
import Timeline from "./components/Timeline";
import Home from "./components/Home";

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  color: #000;
  font-family: "Inter", sans-serif;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgb(183 183 183 / 30%);
  border-radius: 50%;
  border-top-color: black;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin: 10px 0 0 0;
  font-size: 1rem;
  opacity: 0.8;
  text-align: center;
`;

export default function App() {
  const navigate = useNavigate();
  const { resortId: urlResortId } = useParams();
  const location = useLocation();

  const [basemap, setBasemap] = useState("maptiler"); // "carto"
  const [geojsonData, setGeojsonData] = useState([]);
  const [showLines, setShowLines] = useState(true);
  const [showMarkers, setShowMarkers] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [show3D, setShow3D] = useState(true);
  const [resorts, setResorts] = useState([]);
  const [resortsLoaded, setResortsLoaded] = useState(false);
  const [hoverLocation, setHoverLocation] = useState(null);
  const [timestamps, setTimestamps] = useState({});
  const [resortStats, setResortStats] = useState({});
  const [resortRuns, setResortRuns] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  const selectedResortId = urlResortId ?? null;

  // Sync isDemo from navigation state when opening a resort from Home
  useEffect(() => {
    if (selectedResortId && location.state?.isDemo !== undefined) {
      setIsDemo(location.state.isDemo);
    }
  }, [selectedResortId, location.state]);

  const checkAuthentication = () => {
    setLoading(true);
    fetch("/auth/me")
      .then((res) => {
        if (!res.ok) {
          throw new Error("Not authenticated");
        }
        return res.json();
      })
      .then((data) => {
        setUser(data);
        setLoading(false);
        fetchResorts();
      })
      .catch(() => {
        setUser(null);
        setLoading(false);
        navigate("/login");
      });
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  const fetchResorts = async () => {
    try {
      const res = await fetch("/api/resorts");
      const data = await res.json();
      setResorts(data);
    } catch (err) {
      console.error("Failed to fetch resorts:", err);
    } finally {
      setResortsLoaded(true);
    }
  };

  // Memoize the selected resort metadata to prevent unnecessary re-renders
  const selectedResortMetaData = useMemo(() => {
    if (!selectedResortId || !resorts.length) return null;
    return resorts.find((resort) => resort.id === selectedResortId);
  }, [selectedResortId, resorts]);

  useEffect(() => {
    if (!selectedResortId) return;

    setGeojsonData([]);

    async function loadData() {
      try {
        const res = await fetch(
          `/api/resort/${selectedResortId}?isDemo=${isDemo}`
        );
        const { processedFeatures, timestamps, stats, runs } = await res.json();

        setGeojsonData(processedFeatures);
        setTimestamps(timestamps);
        setResortStats(stats);
        setResortRuns(runs || []);
      } catch (err) {
        console.error("Failed to fetch resort data:", err);
      }
    }
    loadData();
  }, [selectedResortId, isDemo, resorts]);

  if (loading) {
    return (
      <>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your mountain adventures...</LoadingText>
        </LoadingContainer>
      </>
    );
  }

  if (selectedResortId && geojsonData.length === 0) {
    return (
      <>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>
            {selectedResortMetaData?.name && selectedResortMetaData?.location
              ? `Traveling to ${selectedResortMetaData.name}, ${selectedResortMetaData.location}...`
              : "Loading resort..."}
          </LoadingText>
        </LoadingContainer>
      </>
    );
  }

  return (
    <>
      <Container>
        {resorts && !selectedResortId ? (
          <Home
            resorts={resorts}
            resortsLoaded={resortsLoaded}
            fetchResorts={fetchResorts}
          />
        ) : (
          <>
            <MapView
              basemap={basemap}
              geojsonData={geojsonData}
              showLines={showLines}
              showMarkers={showMarkers}
              show3D={show3D}
              hoverLocation={hoverLocation}
              selectedResortId={selectedResortId}
              resortRuns={resortRuns}
              setResortRuns={setResortRuns}
              visualizationSettings={selectedResortMetaData?.visualization}
            />
            <Controls
              basemap={basemap}
              setBasemap={setBasemap}
              showLines={showLines}
              setShowLines={setShowLines}
              showMarkers={showMarkers}
              setShowMarkers={setShowMarkers}
              showTimeline={showTimeline}
              setShowTimeline={setShowTimeline}
              show3D={show3D}
              setShow3D={setShow3D}
              resortMetaData={selectedResortMetaData}
              resortStats={resortStats}
              resortRuns={resortRuns}
              onBack={() => navigate("/app")}
              onHoverLocation={setHoverLocation}
              isAdmin={user.isAdmin}
            />
            {Object.keys(timestamps).length > 0 && showTimeline && (
              <Timeline
                groupedTimestamps={timestamps}
                onHover={setHoverLocation}
              />
            )}
          </>
        )}
      </Container>
    </>
  );
}
